import { Link } from "react-router-dom";
import { SERVICE_AREAS, BUSINESS } from "../../../mocks/businessData";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

const REGIONS = [
  {
    name: "New Jersey",
    icon: "ri-map-2-fill",
    highlight: "Newark · Jersey City · Hoboken · Paterson · Clifton",
    areas: SERVICE_AREAS.filter((a) => a.includes("NJ")),
  },
  {
    name: "New York",
    icon: "ri-building-fill",
    highlight: "Manhattan · Brooklyn · Queens · The Bronx · Staten Island",
    areas: SERVICE_AREAS.filter((a) => a.includes("NY")),
  },
  {
    name: "Pennsylvania",
    icon: "ri-map-pin-fill",
    highlight: "Philadelphia & surrounding areas",
    areas: SERVICE_AREAS.filter((a) => a.includes("PA")),
  },
  {
    name: "Connecticut",
    icon: "ri-map-pin-2-fill",
    highlight: "All of Connecticut — or ship to us",
    areas: SERVICE_AREAS.filter((a) => a.includes("CT")),
  },
];

export default function ServiceAreasSection() {
  const ref = useScrollReveal();
  const featured = SERVICE_AREAS.slice(0, 16);

  return (
    <section className="bg-stone-950 py-24" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="reveal flex flex-col lg:flex-row items-start justify-between gap-8 mb-16">
          <div>
            <p className="text-amber-500 text-xs tracking-[0.35em] uppercase font-semibold mb-4">
              Our Service Territory
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-4">
              Serving 40+ Cities Across
              <span className="block italic text-amber-300">NJ, NY, PA &amp; CT</span>
            </h2>
            <div className="gold-rule" />
          </div>
          <div className="lg:text-right max-w-sm lg:max-w-xs">
            <p className="text-stone-400 text-sm leading-relaxed mb-5">
              Can&apos;t find your city? Call us — we&apos;ll arrange service. 
              Not in our area? Ship your treatments directly to our Belleville, NJ facility.
            </p>
            <a
              href="tel:19734891019"
              className="btn-luxury bg-amber-700 hover:bg-amber-600 text-white text-xs tracking-[0.22em] uppercase font-semibold px-7 py-3.5 rounded-sm whitespace-nowrap cursor-pointer inline-block"
            >
              Check Your Area
            </a>
          </div>
        </div>

        {/* Region Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-800 mb-16">
          {REGIONS.map((region, i) => (
            <div
              key={region.name}
              className={`reveal reveal-delay-${i + 1} bg-stone-950 hover:bg-stone-900 transition-colors duration-300 p-8`}
            >
              <div className="w-10 h-10 flex items-center justify-center border border-amber-700/40 bg-amber-900/20 rounded-sm mb-5">
                <i className={`${region.icon} text-amber-500 text-lg`} />
              </div>
              <h3 className="font-serif text-2xl text-white mb-2">{region.name}</h3>
              <p className="text-amber-600/70 text-xs tracking-wide mb-3">
                {region.areas.length}+ cities &amp; areas
              </p>
              <p className="text-stone-500 text-xs leading-relaxed">{region.highlight}</p>
            </div>
          ))}
        </div>

        {/* City grid */}
        <div className="reveal">
          <p className="text-stone-500 text-xs tracking-[0.28em] uppercase font-semibold mb-5">
            Featured Service Cities
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {featured.map((area) => (
              <Link
                key={area}
                to="/location"
                className="flex items-center gap-1.5 px-3 py-2 border border-stone-800 hover:border-amber-700/50 hover:bg-stone-900 rounded-sm transition-all duration-200 group cursor-pointer"
              >
                <span className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-amber-700 text-xs group-hover:text-amber-500" />
                </span>
                <span className="text-stone-400 group-hover:text-stone-200 text-xs whitespace-nowrap transition-colors">
                  {area}
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between">
            <p className="text-stone-600 text-xs">
              +{SERVICE_AREAS.length - 16} more cities covered
            </p>
            <Link
              to="/location"
              className="text-amber-600 hover:text-amber-400 text-xs tracking-widest uppercase font-semibold cursor-pointer transition-colors"
            >
              View All Locations &rarr;
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
